import { useEffect, useState } from 'react';
import './game-plans.styles.scss';
import { Link } from 'react-router-dom';
import { cleanStrategy, DEFENSES, OFFENSES, PACES, strategyLabel } from '@shared/strategy.js';
import { planReach, topScorer, upcomingGames } from '@shared/gamePlan.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { gameDayText } from '@/utils/nextGames';
import { positions } from '@/utils/positions';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// Game plans for the team's next few games: offense, defense and pace for
// one game, plus an opponent to key on. A game without a plan uses the
// usual strategy. Plans are private (plans/{teamId}) until tip-off.
// plans: the plans document (undefined while it loads).
const GamePlans = ({ league, team, teams, schedule, plans, base, openGameId }) => {

    const [ open, setOpen ] = useState(openGameId ?? null);
    const games = upcomingGames(schedule?.games, team.id, league.season.day ?? 0, planReach(league));
    const usual = cleanStrategy(plans?.usual ?? team.strategy);

    useEffect(() => {
        if (openGameId) setOpen(openGameId);
    }, [openGameId])

    if (plans === undefined) return <div className='g-card'><ThreeDotsLoader /></div>;

    return (
        <div className='g-card game-plans' id='game-plans'>
            <h2>Game plans <HelpLink section='game-plans' label='How game plans work' /></h2>
            <p className='meta'>
                Scout your next opponents and set a plan for each game. Games without a plan use your usual strategy (below).
                Opponents can&apos;t see your plan until the game tips off.
            </p>
            {games.length === 0 ?
                <p className='meta'>No games coming up right now.</p> :
                <ul className='plan-list'>
                    {games.map(g => (
                        <PlanRow key={g.id} game={g} league={league} team={team} teams={teams} base={base}
                            usual={usual} plan={plans?.games?.[g.id]} open={open === g.id}
                            onToggle={() => setOpen(open === g.id ? null : g.id)} />
                    ))}
                </ul>
            }
        </div>
    )
}

// One upcoming game: opponent, tip-off and the plan, with the editor
// below when open.
const PlanRow = ({ game, league, team, teams, base, usual, plan, open, onToggle }) => {
    const home = game.home === team.id;
    const oppId = home ? game.away : game.home;
    const opp = teams?.[oppId];
    const when = gameDayText(league, game.day);
    return (
        <li className={`plan-row ${open ? 'open' : ''}`}>
            <div className='plan-head'>
                <TeamBadge team={opp} size='sm' />
                <div className='plan-game'>
                    <div>
                        {home ? 'vs' : '@'} <TeamName team={opp} id={oppId} />
                    </div>
                    <div className='meta'>{when ?? `Day ${game.day}`}{game.label ? ` · ${game.label}` : ''}</div>
                    <div className={`plan-summary ${plan ? 'set' : ''}`}>
                        {plan ? planText(plan) : 'Usual strategy'}
                    </div>
                </div>
                <div className='plan-actions'>
                    <Link to={`${base}/teams/${oppId}/scout`} className='g-button'>Scout</Link>
                    <button className={`g-button ${open ? '' : 'primary'}`} aria-expanded={open} onClick={onToggle}>
                        {open ? 'Close' : plan ? 'Edit' : 'Plan'}
                    </button>
                </div>
            </div>
            {open && <PlanEditor league={league} game={game} oppId={oppId} usual={usual} plan={plan} onDone={onToggle} />}
        </li>
    )
}

// "Post-up · Zone · Slow pace · Keying on J. Smith"
export function planText(plan) {
    const parts = [
        strategyLabel(OFFENSES, plan.offense),
        strategyLabel(DEFENSES, plan.defense),
        `${strategyLabel(PACES, plan.pace)} pace`,
    ];
    if (plan.keyOn) parts.push(`Keying on ${plan.keyOnName ?? 'a player'}`);
    return parts.join(' · ');
}

const PlanEditor = ({ league, game, oppId, usual, plan, onDone }) => {
    const [ opp, setOpp ] = useState(undefined);
    const [ draft, setDraft ] = useState({
        offense: plan?.offense ?? usual.offense,
        defense: plan?.defense ?? usual.defense,
        pace: plan?.pace ?? usual.pace,
        keyOn: plan?.keyOn ?? '',
    });
    const [ saving, setSaving ] = useState(false);
    const [ message, setMessage ] = useState(null);

    // The opponent's roster, for choosing who to key on.
    useEffect(() => {
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/teams/${oppId}`, 'callback': setOpp, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id, oppId])

    const players = opp ? Object.values(opp.players)
        .sort((a, b) => ppg(b) - ppg(a) || b.overall - a.overall) : [];
    const top = opp ? topScorer(opp).id : null;

    async function save(clear) {
        setSaving(true);
        setMessage(null);
        const res = await UseCloudFunction('savePlan', {
            leagueId: league.id, gameId: game.id,
            plan: clear ? null : { ...draft, keyOn: draft.keyOn || null },
        });
        setSaving(false);
        if (res.error) setMessage({ error: true, text: res.error });
        else onDone();
    }

    const select = (cat, title, list) => (
        <label className='plan-field'>
            <span>{title}</span>
            <select value={draft[cat]} onChange={e => setDraft({ ...draft, [cat]: e.target.value })}>
                {list.map(o => (
                    <option key={o.key} value={o.key}>{o.label}{o.key === usual[cat] ? ' (usual)' : ''}</option>
                ))}
            </select>
        </label>
    );

    return (
        <div className='plan-editor'>
            <div className='plan-fields'>
                {select('offense', 'Offense', OFFENSES)}
                {select('defense', 'Defense', DEFENSES)}
                {select('pace', 'Pace', PACES)}
                <label className='plan-field'>
                    <span>Key on</span>
                    {opp === undefined ? <ThreeDotsLoader /> :
                        <select value={draft.keyOn} onChange={e => setDraft({ ...draft, keyOn: e.target.value })}>
                            <option value=''>Nobody: play it straight</option>
                            {players.map(p => (
                                <option key={p.id} value={p.id} disabled={p.injury?.gamesOut > 0}>
                                    {p.fName} {p.lName} ({positions[p.pos1 - 1].short}{p.season?.gp ? `, ${ppg(p).toFixed(1)} ppg` : ''})
                                    {p.id === top ? ' · top scorer' : ''}{p.injury?.gamesOut > 0 ? ' · injured' : ''}
                                </option>
                            ))}
                        </select>
                    }
                </label>
            </div>
            <p className='meta'>
                Keying on a player takes away their shots and makes them work for every basket, but the other four get better looks.
                It pays off against a team that leans on one scorer and backfires against a balanced one.
            </p>
            <div className='plan-buttons'>
                <span role='status' className={message?.error ? 'error' : 'meta'}>{message?.text}</span>
                {plan && <button className='g-button' disabled={saving} onClick={() => save(true)}>Use my usual strategy</button>}
                <button className={`g-button primary ${saving ? 'disabled' : ''}`} disabled={saving} onClick={() => save(false)}>
                    {saving ? <ThreeDotsLoader /> : 'Save plan'}
                </button>
            </div>
        </div>
    )
}

const ppg = (p) => (p.season?.gp ? p.season.pts / p.season.gp : 0);

export default GamePlans
